import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, FileUp, Info } from 'lucide-react';
import { Button, Labeled, Modal } from '@tes/ui';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import type { BaseFileSwap, FlaggedField, Template } from '../lib/types';

function FieldList({ fields }: { fields: FlaggedField[] }) {
  return (
    <ul className="mt-1 space-y-0.5">
      {fields.map((f) => (
        <li key={f.id} className="truncate">
          <span className="font-medium">{f.name}</span>
          <span className="opacity-70">
            {' '}
            · page {f.page}
            {f.overflow &&
              ` · ${[
                f.overflow.right > 0 && `${f.overflow.right}pt past the right edge`,
                f.overflow.bottom > 0 && `${f.overflow.bottom}pt past the bottom`,
              ]
                .filter(Boolean)
                .join(', ')}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Swap the base PDF while the design stays put. Runs a dry run first so the
 * author sees exactly what the real swap will do before committing.
 */
export function ReplaceBaseFileDialog({
  templateId,
  onClose,
  onReplaced,
  saveFirst,
}: {
  templateId: string;
  onClose: () => void;
  onReplaced: (t: Template) => void;
  /**
   * Persists pending edits (design first, then settings — see EditorPage.saveAll).
   * A failure here is reported as "could not save" rather than as a file problem.
   */
  saveFirst: () => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<BaseFileSwap | null>(null);

  const check = useMutation({
    mutationFn: async (chosen: File) => {
      // Persist pending edits first: the swap is planned against the SAVED
      // fields, so an unsaved move would be planned from stale coordinates and
      // then thrown away when the editor re-initialises from the response.
      try {
        await saveFirst();
      } catch (e) {
        throw new Error(`Could not save the design before checking the file: ${messageOf(e, 'save failed')}`);
      }
      return api.replaceBaseFile(templateId, chosen, { dryRun: true });
    },
    onSuccess: setPlan,
  });

  const apply = useMutation({
    mutationFn: () => api.replaceBaseFile(templateId, file!, { dropOrphanedFields: true }),
    onSuccess: (res) => {
      if (res.template) onReplaced(res.template);
    },
  });

  const pick = (chosen: File | null) => {
    setFile(chosen);
    setPlan(null);
    check.reset();
    apply.reset();
    if (chosen) check.mutate(chosen);
  };

  const busy = check.isPending || apply.isPending;
  const pageCountChanged = plan && plan.pages.before !== plan.pages.after;

  return (
    <Modal
      title="Replace base file"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={plan?.orphanedFields.length ? 'danger' : 'primary'}
            disabled={!plan || busy}
            onClick={() => apply.mutate()}
          >
            <FileUp size={16} className="mr-1" />
            {apply.isPending
              ? 'Replacing…'
              : plan?.orphanedFields.length
                ? 'Continue anyway'
                : 'Replace base file'}
          </Button>
        </>
      }
    >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Every placed field keeps its exact position — the new pages render underneath them.
            Already-published versions are untouched until you publish again.
          </p>

          <Labeled label="New base file (PDF, PNG, or JPEG)">
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
              className="w-full text-sm"
            />
          </Labeled>

          {check.isPending && <p className="text-sm text-slate-400">Checking the new file…</p>}
          {check.error && (
            <p className="text-sm text-red-600">{messageOf(check.error, 'Could not read that file')}</p>
          )}

          {plan && (
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-slate-600">
                <Info size={16} className="mt-0.5 shrink-0" />
                <span>
                  {plan.pages.before} page{plan.pages.before === 1 ? '' : 's'} →{' '}
                  <span className="font-medium text-slate-900">
                    {plan.pages.after} page{plan.pages.after === 1 ? '' : 's'}
                  </span>
                  {plan.resizedPages.length > 0 && (
                    <>
                      {' · '}
                      {plan.resizedPages.length} page
                      {plan.resizedPages.length === 1 ? '' : 's'} changed size
                    </>
                  )}
                  {!pageCountChanged &&
                    plan.resizedPages.length === 0 &&
                    plan.outOfBoundsFields.length === 0 && ' · nothing moves'}
                </span>
              </div>

              {plan.orphanedFields.length > 0 && (
                <div className="flex gap-2 rounded-lg bg-red-50 p-3 text-red-800">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium">
                      {plan.orphanedFields.length} field
                      {plan.orphanedFields.length === 1 ? '' : 's'} will be deleted
                    </span>{' '}
                    — placed on pages the new file doesn&apos;t have.
                    <FieldList fields={plan.orphanedFields} />
                  </div>
                </div>
              )}

              {plan.outOfBoundsFields.length > 0 && (
                <div className="flex gap-2 rounded-lg bg-amber-50 p-3 text-amber-800">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium">
                      {plan.outOfBoundsFields.length} field
                      {plan.outOfBoundsFields.length === 1 ? '' : 's'} now hang
                      {plan.outOfBoundsFields.length === 1 ? 's' : ''} off the page
                    </span>{' '}
                    — kept where they are, but they&apos;ll stamp clipped until you move them.
                    <FieldList fields={plan.outOfBoundsFields} />
                  </div>
                </div>
              )}
            </div>
          )}

          {apply.error && (
            <p className="text-sm text-red-600">{messageOf(apply.error, 'Replace failed')}</p>
          )}
        </div>
    </Modal>
  );
}
